/**
 * Phase 98ay (10/07/2026) — Modale diagnostic « lignes fantômes » d'une tournée.
 *
 * Contexte du bug : l'utilisateur voit un produit nommé « 23 » apparaître dans
 * le PDF de préparation tournée pour un distributeur (typiquement Sion Piscine)
 * sans savoir d'où il vient. Cette modale liste toutes les lignes suspectes
 * détectées par `/api/refill-tours/{id}/lines-diagnose` :
 *   - Produits inexistants (ID orphelin)
 *   - Produits archivés (soft-deleted)
 *   - Noms vides ou uniquement numériques ≤4 chars
 *
 * Depuis la modale, l'admin peut lancer un nettoyage automatique
 * (`/lines-cleanup`) qui purge les lignes du tour ET les entrées orphelines
 * de `displayed_product_rails` du distributeur.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { AlertTriangle, CheckCircle2, Loader2, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

export default function PhantomLinesDiagnostic({ tourId, open, onClose, onCleaned }) {
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [result, setResult] = useState(null);
  const confirm = useConfirm();

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/refill-tours/${tourId}/lines-diagnose`);
        setResult(data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Erreur diagnostic");
      } finally {
        setLoading(false);
      }
    })();
  }, [tourId, open]);

  const runCleanup = async () => {
    const nbLines = result?.suspicious_lines_count || 0;
    const nbRails = result?.suspicious_rails_count || 0;
    const ok = await confirm({
      title: "Nettoyer les lignes fantômes ?",
      description:
        `${nbLines} ligne(s) fantôme(s) et ${nbRails} rail(s) orphelin(s) vont être supprimés.\n\n` +
        "Cette action est irréversible. Les rails supprimés du distributeur ne seront plus proposés dans les futures tournées.",
      confirmText: "Nettoyer",
      destructive: true,
    });
    if (!ok) return;
    setCleaning(true);
    try {
      const { data } = await api.post(`/refill-tours/${tourId}/lines-cleanup`);
      toast.success(
        `Nettoyage OK : ${data.removed_lines_count} ligne(s) et ${data.removed_rails_count} rail(s) supprimés`
      );
      onCleaned && onCleaned();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur nettoyage");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-3xl" data-testid="phantom-diagnostic-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="w-5 h-5 text-[#8C6B2F]" />
            Diagnostic — lignes suspectes
          </DialogTitle>
          <DialogDescription>
            Recherche les produits fantômes (nom vide, uniquement numérique, archivé ou inexistant)
            dans les lignes de la tournée et les rails des distributeurs concernés.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="p-6 text-center text-[#575A57]">
            <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Analyse en cours…
          </div>
        )}

        {!loading && result && (
          <div className="space-y-4">
            {/* Résumé */}
            <div className="grid grid-cols-3 gap-3">
              <SummaryCard
                icon={<CheckCircle2 className="w-4 h-4 text-[#1F4E3D]" />}
                label="Lignes saines"
                value={result.healthy_lines_count}
                color="#1F4E3D"
              />
              <SummaryCard
                icon={<AlertTriangle className="w-4 h-4 text-[#C05C47]" />}
                label="Lignes fantômes"
                value={result.suspicious_lines_count}
                color="#C05C47"
                warn={result.suspicious_lines_count > 0}
              />
              <SummaryCard
                icon={<AlertTriangle className="w-4 h-4 text-[#8C6B2F]" />}
                label="Rails orphelins"
                value={result.suspicious_rails_count}
                color="#8C6B2F"
                warn={result.suspicious_rails_count > 0}
              />
            </div>

            {/* Empty state */}
            {result.suspicious_lines_count === 0 && result.suspicious_rails_count === 0 && (
              <div className="p-6 text-center bg-[#EAF4EE] border border-[#C5DBCF]" data-testid="phantom-empty">
                <CheckCircle2 className="w-8 h-8 mx-auto text-[#1F4E3D] mb-2" />
                <p className="font-medium text-[#1F4E3D]">
                  Aucune ligne suspecte détectée — la tournée est propre 🌿
                </p>
              </div>
            )}

            {/* Lignes suspectes du tour */}
            {result.suspicious_lines?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-[#C05C47] mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Lignes fantômes dans la tournée
                </h3>
                <div className="border border-[#E5E5E0] overflow-hidden">
                  <table className="w-full text-xs" data-testid="phantom-lines-table">
                    <thead className="bg-[#FAFAF7]">
                      <tr>
                        <th className="text-left px-2 py-2">Distributeur</th>
                        <th className="text-left px-2 py-2">Slot</th>
                        <th className="text-left px-2 py-2">Produit</th>
                        <th className="text-left px-2 py-2">SKU</th>
                        <th className="text-center px-2 py-2">Qté</th>
                        <th className="text-left px-2 py-2">Raison(s)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.suspicious_lines.map((l, i) => (
                        <tr key={i} className="border-t border-[#E5E5E0]">
                          <td className="px-2 py-1.5">
                            {l.distributor_code} · {l.distributor_name}
                          </td>
                          <td className="px-2 py-1.5 font-mono font-semibold">{l.slot_number || "—"}</td>
                          <td className="px-2 py-1.5">
                            <div className="font-medium">{l.product_name || <em className="text-[#C05C47]">(vide)</em>}</div>
                            <div className="text-[10px] text-[#6B6E6B] font-mono">{l.product_id?.slice(0, 8)}…</div>
                          </td>
                          <td className="px-2 py-1.5 font-mono text-[10px]">{l.product_sku || "—"}</td>
                          <td className="px-2 py-1.5 text-center font-mono">
                            {l.qty_loaded || l.qty_planned || 0}
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="flex flex-wrap gap-1">
                              {l.reasons.map((r, j) => (
                                <Badge
                                  key={j}
                                  variant="outline"
                                  className="bg-[#FBEDEA] text-[#C05C47] border-[#E8CDC5] text-[10px]"
                                >
                                  {r}
                                </Badge>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Rails orphelins des distributeurs */}
            {result.suspicious_rails?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm text-[#8C6B2F] mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Rails orphelins des distributeurs
                </h3>
                <div className="border border-[#E5E5E0] overflow-hidden">
                  <table className="w-full text-xs" data-testid="phantom-rails-table">
                    <thead className="bg-[#FAFAF7]">
                      <tr>
                        <th className="text-left px-2 py-2">Distributeur</th>
                        <th className="text-left px-2 py-2">Rail (slot)</th>
                        <th className="text-left px-2 py-2">Produit référencé</th>
                        <th className="text-left px-2 py-2">Problème</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.suspicious_rails.map((r, i) => (
                        <tr key={i} className="border-t border-[#E5E5E0]">
                          <td className="px-2 py-1.5">{r.distributor_name}</td>
                          <td className="px-2 py-1.5 font-mono font-semibold">{r.rail || "—"}</td>
                          <td className="px-2 py-1.5">
                            <div className="font-medium">
                              {r.product_name || <em className="text-[#8C6B2F]">(vide)</em>}
                            </div>
                            <div className="text-[10px] text-[#6B6E6B] font-mono">
                              {r.product_id?.slice(0, 8)}…
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <Badge variant="outline" className="bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D9B8] text-[10px]">
                              {r.issue}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="phantom-close-btn">
            Fermer
          </Button>
          {result &&
            (result.suspicious_lines_count > 0 || result.suspicious_rails_count > 0) && (
              <Button
                onClick={runCleanup}
                disabled={cleaning}
                className="bg-[#C05C47] hover:bg-[#A04E2C]"
                data-testid="phantom-cleanup-btn"
              >
                {cleaning ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Nettoyer les fantômes
              </Button>
            )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCard({ icon, label, value, color, warn }) {
  return (
    <div
      className="border p-3"
      style={{
        borderColor: warn ? color : "#E5E5E0",
        background: warn ? `${color}0F` : "white",
      }}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#6B6E6B]">
        {icon} {label}
      </div>
      <div className="text-2xl font-mono font-semibold mt-1" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
